import { useMemo, useState } from "react";
import {
  formatDate,
  MappingStatus,
  MappingStyles,
} from "../../../../utils/utils";
import { X } from "lucide-react";

export default function RejectedTable({
  selectedAll,
  selectedApplicants,
  handleSelectAll,
  filteredApplicants,
  handleSelectApplicant,
  handleViewApplication,
}) {
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  // Ensure applicantsToRender is always an array
  const applicantsToRender = useMemo(
    () => (Array.isArray(filteredApplicants) ? filteredApplicants : []),
    [filteredApplicants]
  );

  const noData = applicantsToRender.length === 0;

  console.log("Rendering RejectedTable with applicants:", applicantsToRender);

  const handleCreditLimitClick = (rejectionReason) => {
    console.log('Credit limit clicked, rejection reason:', rejectionReason);
    setSelectedReason(rejectionReason || "No rejection reason provided.");
    setShowReasonModal(true);
  };

  const handleCloseModal = () => {
    setShowReasonModal(false);
    setSelectedReason("");
  };

  return (
    <div className="overflow-x-auto border border-[#333333] bg-[#131313] rounded-lg m-1 scrollbar-hide">
      <table className="w-full min-w-[800px] text-sm text-white border-separate border-spacing-0">
        <thead className="bg-[#131313]">
          <tr>
            <th className="p-4 text-center border-x border-[#333333] rounded-tl-lg">
              <input
                type="checkbox"
                className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 relative cursor-pointer
              before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                checked={selectedAll}
                onChange={handleSelectAll}
                disabled={noData}
              />
            </th>
            {[
              "APPLICANT NAME",
              "LOAN AMOUNT",
              "CREDIT LIMIT",
              "PROCESSING TYPE",
              "BANK NAME",
              "REJECTED ON",
              "ACTIONS",
            ].map((title, i) => (
              <th
                key={title}
                className={`p-4 text-center border-x border-[#333333] ${
                  i === 6 ? "rounded-tr-lg" : ""
                }`}
              >
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {noData ? (
            <tr>
              <td
                colSpan="8"
                className="p-6 text-center text-gray-400 border-x border-[#333333]"
              >
                No values to display now.
              </td>
            </tr>
          ) : (
            applicantsToRender.map((applicant, index) => {
              const isLast = index === applicantsToRender.length - 1;
              return (
                <tr
                  key={applicant._id || index}
                  className={`bg-[#131313] border-b border-[#333333] ${
                    isLast ? "last:rounded-b-lg" : ""
                  }`}
                >
                  <td className="p-4 text-center border-x border-[#333333]">
                    <input
                      type="checkbox"
                      className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 relative cursor-pointer
                    before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                      checked={selectedApplicants.includes(applicant._id)}
                      onChange={() => handleSelectApplicant(applicant._id)}
                    />
                  </td>
                  <td className="p-4 text-center border-x border-[#333333]">
                    <div className="flex flex-col items-center gap-1">
                      {applicant.user?.name || applicant?.username || "N/A"}
                      {applicant.referredBy && (
                        <span className="text-xs text-[#FFCA20]">
                          Affiliate Application
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-center border-x border-[#333333]">
                    <div>
                      <div>{applicant.principle}</div>
                      <div className="text-sm text-gray-400">
                        {applicant.loanType}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-center border-x border-[#333333]">
                    <span
                      className={`px-3 py-1 rounded-full text-sm cursor-pointer hover:opacity-80 transition-opacity ${MappingStyles(
                        applicant.status
                      )}`}
                      onClick={() => handleCreditLimitClick(applicant.rejection_reason)}
                      title="Click to view rejection reason"
                    >
                      {MappingStatus(applicant.status)}
                    </span>
                  </td>
                  <td className="p-4 text-center border-x border-[#333333]">
                    {applicant.processingMethod}
                  </td>
                  <td className="p-4 text-center border-x border-[#333333]">
                    {applicant.bankName || "N/A"}
                  </td>
                  <td className="p-4 text-center border-x border-[#333333]">
                    {formatDate(applicant.rejectedAt || applicant.updatedAt)}
                  </td>
                  <td className="p-4 text-center border-x border-[#333333]">
                    <button
                      className="text-[#FFCA20] hover:text-[#FFCA20]"
                      onClick={() => handleViewApplication(applicant._id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Rejection Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-[#131313] rounded-lg p-6 w-96 max-w-[90vw] border border-[#333333]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold">Rejection Reason</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-white text-sm">
                {selectedReason || "No rejection reason provided."}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-[#FFCA20] text-black rounded-md hover:bg-yellow-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
